import { ActorMovementType } from "./ActorMovementType";
import { Vec2D } from "./Vec2D";

export const propsMapped = new Map<string, string>([
  ["None", "/actor_props/none.png"],
  ["CoffeeCup", "/actor_props/coffee_cup.png"],
  ["Clipboard", "/actor_props/clipboard.png"],
]);

export const actorStates: string[] = ["Normal", "Hyperventilating"];

export const actorProps: string[] = ["None", "CoffeeCup", "Clipboard"];

export const defaultActorStateAnimationStateName = "";
export const defaultActorStateAnimationLayer = 0;

export interface ActorStateAnimation {
  /** The API animation's display name as it was when picked (not the GUID). */
  id: string;
  /** Animator state; with `layer` it defines the animation. */
  stateName: string;
  layer: number;
  /**
   * The API's `externalId` for this state and layer. Added lazily by
   * `ensureAnimationIds` on load; never derived here.
   */
  animationId?: string;
}

export const defaultActorStateAnimation: ActorStateAnimation = {
  id: "",
  stateName: defaultActorStateAnimationStateName,
  layer: defaultActorStateAnimationLayer,
};

// stores the state of an actor in between takes
export interface ActorState {
  id: string;
  actorId: string;
  positionId: string;
  direction?: Vec2D;
  movement: ActorMovementType;
  lookat: string;
  /** Look at `lookat` with the head only (FinalIK); missing/false = whole body. */
  finalIK?: boolean;
  outfit: string;
  state: string;
  /** Legacy, no longer written: older trainings may still carry "None", "NormalTalkingLoop" or "Thinking". */
  activity?: string;
  prop: string;
  resetCharacterParts: boolean;
  /** Whether the player sees the Explore indicator after the configured delay. */
  exploreShowIndicator?: boolean;
  /** Delay in seconds before showing the Explore indicator. */
  exploreIndicatorDelay?: number;
  /** Distance from the destination that completes player Explore movement. */
  exploreArrivalDistance?: number;
  animation: ActorStateAnimation;
}

type NormalizableActorState = Omit<
  ActorState,
  "animation" | "resetCharacterParts"
> &
  Partial<Pick<ActorState, "animation" | "resetCharacterParts">>;

export function normalizeActorState(
  actorState: ActorState | NormalizableActorState
): ActorState {
  return {
    ...actorState,
    movement:
      actorState.actorId !== "player" &&
      (actorState.movement === ActorMovementType.AutoWalk ||
        actorState.movement === ActorMovementType.Explore)
        ? ActorMovementType.Walk
        : actorState.movement,
    resetCharacterParts: actorState.resetCharacterParts ?? false,
    exploreArrivalDistance:
      typeof actorState.exploreArrivalDistance === "number" &&
      Number.isFinite(actorState.exploreArrivalDistance) && actorState.exploreArrivalDistance > 0
        ? actorState.exploreArrivalDistance : 2,
    exploreShowIndicator: actorState.exploreShowIndicator ?? false,
    exploreIndicatorDelay:
      typeof actorState.exploreIndicatorDelay === "number" &&
      Number.isFinite(actorState.exploreIndicatorDelay) &&
      actorState.exploreIndicatorDelay >= 0
        ? actorState.exploreIndicatorDelay
        : 10,
    animation: actorState.animation ?? defaultActorStateAnimation,
  };
}
